import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import cx from 'classnames';
import { MdClose } from 'react-icons/md';
import { MediaType } from '../../../codegen/graphql';
import { Modal } from '../common/Modal';
import Button from '../common/Button';
import { Lightbox } from '../ImageTools/Lightbox';
import { uploadMediaTranslation } from '../../utils/imageApi';
import { useModalRouter } from '../../ModalRouter_context';

export const TRANSLATION_LANGUAGES = ['FR', 'IT', 'ES', 'NL', 'DE', 'PT'] as const;
type TranslationLang = typeof TRANSLATION_LANGUAGES[number];

export const GET_MEDIA_TRANSLATIONS = gql`
  query MediaTranslationsManager_GetMedia($mediaId: ID!) {
    media(id: $mediaId) {
      id
      url
      type
      width
      height
      translations {
        id
        lang
        url
        langMediaActive
        type
        filepath
        createdAt
      }
      translationJobs {
        id
        status
        createdAt
        sources {
          mediaId
          targetLanguage
        }
      }
    }
  }
`;

const DELETE_MEDIA_TRANSLATION = gql`
  mutation MediaTranslationsManager_DeleteMediaTranslation($id: ID!) {
    deleteMediaTranslation(id: $id)
  }
`;

const TERMINAL_JOB_STATUSES = new Set([
  'JOB_STATE_SUCCEEDED',
  'JOB_STATE_FAILED',
  'JOB_STATE_CANCELLED',
  'JOB_STATE_EXPIRED',
]);

type MediaTranslationItem = {
  id: string;
  lang: string;
  url: string;
  langMediaActive: boolean;
  type: string;
  filepath: string;
  createdAt: string;
};

type MediaTranslationJobItem = {
  id: string;
  status: string;
  createdAt: string;
  sources: Array<{
    mediaId: string;
    targetLanguage: string;
  }>;
};

type MediaTranslationsManagerQuery = {
  media: {
    id: string;
    url: string;
    type: MediaType;
    width: number | null;
    height: number | null;
    translations: Array<MediaTranslationItem>;
    translationJobs: Array<MediaTranslationJobItem>;
  } | null;
};

interface MediaTranslationsManagerModalProps {
  mediaId: string;
}

export function MediaTranslationsManagerModal({
  mediaId,
}: MediaTranslationsManagerModalProps) {
  const modalRouter = useModalRouter();
  return (
    <Modal
      title="Media Translations"
      onClose={modalRouter.pop}
      width="xlarge"
      noContentPadding
    >
      <MediaTranslationsManagerModalContent mediaId={mediaId} />
    </Modal>
  );
}

function MediaTranslationsManagerModalContent({
  mediaId,
}: {
  mediaId: string;
}) {
  const modalRouter = useModalRouter();
  const { data, refetch } = useSuspenseQuery<MediaTranslationsManagerQuery>(GET_MEDIA_TRANSLATIONS, {
    variables: { mediaId },
  });

  const media = data.media;

  const translatingLangs = useMemo(() => {
    const langs = new Set<string>();
    for (const job of media?.translationJobs || []) {
      if (TERMINAL_JOB_STATUSES.has(job.status)) continue;
      for (const source of job.sources || []) {
        if (source.mediaId === mediaId) {
          langs.add(source.targetLanguage);
        }
      }
    }
    return langs;
  }, [media?.translationJobs, mediaId]);

  const hasRunningJob = translatingLangs.size > 0;

  useEffect(() => {
    if (!hasRunningJob) return;
    const intervalId = setInterval(() => {
      refetch();
    }, 5000);
    return () => clearInterval(intervalId);
  }, [hasRunningJob, refetch]);
  const [selectedLang, setSelectedLang] = useState<TranslationLang>('FR');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteMediaTranslation, { loading: isDeleting }] = useMutation(DELETE_MEDIA_TRANSLATION);

  const activeTranslationByLang = useMemo(() => {
    const byLang = new Map<string, MediaTranslationItem>();
    for (const translation of media?.translations || []) {
      if (translation.langMediaActive && !byLang.has(translation.lang)) {
        byLang.set(translation.lang, translation);
      }
    }
    return byLang;
  }, [media?.translations]);

  const activeTranslation = activeTranslationByLang.get(selectedLang) ?? null;

  if (!media) {
    return (
      <div className="p-6 text-gray-500">
        Media not found.
      </div>
    );
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteTranslation = async () => {
    if (!activeTranslation || isDeleting) return;
    if (!confirm('Are you sure you want to delete this translation?')) return;

    try {
      await deleteMediaTranslation({
        variables: { id: activeTranslation.id },
        refetchQueries: ['ImagesSection_Main'],
      });
      setLightboxOpen(false);
      await refetch();
    } catch (error) {
      console.error('Error deleting media translation:', error);
      alert('Failed to delete translation image. Please try again.');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    try {
      setIsUploading(true);
      await uploadMediaTranslation(file, mediaId, selectedLang);
      await refetch();
    } catch (error) {
      console.error('Error uploading media translation:', error);
      alert('Failed to upload translation image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 p-3 border-b border-gray-200">
        {TRANSLATION_LANGUAGES.map((lang) => {
          const hasActiveTranslation = activeTranslationByLang.has(lang);
          const isSelected = selectedLang === lang;
          const isTranslating = translatingLangs.has(lang);
          return (
            <Button
              key={lang}
              preset={isSelected ? 'primary' : 'white'}
              size="sm"
              className="w-full justify-center font-normal py-2"
              onPress={() => setSelectedLang(lang)}
            >
              <span className="flex flex-col items-center leading-tight">
                <span className="font-semibold">{lang}</span>
                <span className={cx(
                  'text-[11px] font-normal',
                  isTranslating
                    ? 'text-amber-600'
                    : hasActiveTranslation ? 'text-green-700' : 'text-red-600',
                )}>
                  {isTranslating ? 'Translating' : hasActiveTranslation ? 'Active' : 'Missing'}
                </span>
              </span>
            </Button>
          );
        })}
      </div>

      <div className="flex flex-col md:flex-row md:h-[36rem]">
        <div className="relative h-80 md:h-auto md:w-1/2 border-b md:border-b-0 md:border-r border-gray-200 bg-gray-50">
          {media.type === MediaType.Video ? (
            <video
              src={media.url}
              className="absolute inset-0 w-full h-full object-contain p-6"
              controls
              playsInline
            />
          ) : (
            <img
              src={media.url}
              alt="Source media"
              className="absolute inset-0 w-full h-full object-contain p-6"
            />
          )}
          {media.type !== MediaType.Video && (
            <div className="absolute bottom-4 left-0 right-0 z-10 flex justify-center">
              <Button
                preset="primary"
                size="sm"
                onPress={() => modalRouter.push({
                  s: 'nanoBananaMediasTranslation',
                  a: { mediaIds: [media.id] },
                })}
              >
                Translate
              </Button>
            </div>
          )}
        </div>

        <div className="relative h-80 md:h-auto md:w-1/2 bg-white">
          {activeTranslation ? (
            <>
              <img
                src={activeTranslation.url}
                alt={`${selectedLang} translation`}
                className="absolute inset-0 w-full h-full object-contain p-6 cursor-pointer"
                onClick={() => setLightboxOpen(true)}
              />
              <button
                type="button"
                aria-label="Delete translation"
                disabled={isDeleting}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDeleteTranslation();
                }}
                className="absolute top-4 right-4 z-10 p-1.5 bg-red-600 text-white rounded-full shadow disabled:opacity-50"
              >
                <MdClose size={20} />
              </button>
            </>
          ) : (
            <>
              {media.type === MediaType.Video ? (
                <video
                  src={media.url}
                  className="absolute inset-0 w-full h-full object-contain p-6 opacity-25"
                  muted
                  playsInline
                />
              ) : (
                <img
                  src={media.url}
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain p-6 opacity-25"
                />
              )}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <p className="text-sm font-medium text-gray-800 bg-white/90 px-4 py-2 rounded-md shadow-sm">
                  The source image will be used
                </p>
                <Button
                  preset="primary"
                  size="sm"
                  onPress={handleUploadClick}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading…' : 'Upload image'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {lightboxOpen && activeTranslation && (
        <Lightbox
          className="!z-[1100]"
          medias={[{
            id: activeTranslation.id,
            url: activeTranslation.url,
            type: MediaType.Image,
          }]}
          initialIndex={0}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
}
